import { Chip } from '@heroui/react';
import React from 'react';

export interface UserStatusProps {
  /** Whether the user has requested to be deleted */
  hasRequestedToBeDeleted?: boolean | null;
  /** Whether the user is pending activation */
  pending?: boolean | null;
  /** Size variant of the status indicator */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show only the dot or include text */
  variant?: 'dot' | 'chip';
  /** Custom className for styling */
  className?: string;
}

export const UserStatus: React.FC<UserStatusProps> = ({
  hasRequestedToBeDeleted,
  pending,
  size = 'sm',
  variant = 'chip',
  className = '',
}) => {
  // Determine status and styling based on user state
  const getStatusConfig = () => {
    if (hasRequestedToBeDeleted) {
      return {
        color: 'danger' as const,
        label: 'Deleted',
        dotColor: 'bg-danger-500',
      };
    }

    if (pending) {
      return {
        color: 'warning' as const,
        label: 'Pending',
        dotColor: 'bg-warning-500',
      };
    }

    return {
      color: 'success' as const,
      label: 'Active',
      dotColor: 'bg-success-500',
    };
  };

  const config = getStatusConfig();

  if (variant === 'dot') {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <div
          className={`w-2 h-2 rounded-full ${config.dotColor}`}
          aria-label={`User status: ${config.label}`}
        />
        <span className="text-sm text-foreground pl-1">{config.label}</span>
      </div>
    );
  }

  return (
    <Chip
      color={config.color}
      variant="flat"
      size={size}
      className={className}
      classNames={{
        base: 'px-3 py-1',
        content: 'px-1',
      }}
      startContent={
        <div className={`w-2 h-2 rounded-full ${config.dotColor}`} />
      }
    >
      {config.label}
    </Chip>
  );
};
